import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { kitLogin, kitRetryAfterSeconds, requestKitPinResetByEmail } from '../api/kitAuth'
import { useAuth } from '../auth/AuthContext'
import { PIN_LENGTH, PinDots, PinKeypad, PinStatus } from '../components/PinPad'
import { DEVICE_BINDING_COPY } from '../kit/deviceCopy'
import { useTabletContext } from './TabletLayout'
import { FALLBACK_LOCKOUT_S, useLockoutCountdown } from '../kit/useLockoutCountdown'
import { DEV_TEST_ACCESS, isLocalDev } from '../kit/devTestAccess'
import { DevTestAccessHint } from '../kit/KitDeviceScreen'

/**
 * Pantalla de entrada del jugador en la tablet compartida: PIN de 6 dígitos
 * y nada más. No hay roster (el PIN identifica solo) ni email/password: en
 * un dispositivo que rota entre 20 jugadores, mostrar la lista del plantel
 * sería filtrar quién entrena y darle a cualquiera la mitad de la credencial.
 *
 * Teclado propio en pantalla (no el nativo): `components/PinPad.tsx`, el
 * mismo pad que usa la entrada del jugador en `/login`.
 */

export function TabletPinScreen() {
  const { access, onLoggedIn, unbindDevice } = useTabletContext()
  const { refresh } = useAuth()

  const [digits, setDigits] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Cada intento fallido sube el nonce: es lo que dispara el shake de los
  // puntos (el texto solo, en un slot de alto fijo, puede pasar inadvertido
  // si el jugador repite el mismo PIN).
  const [errorNonce, setErrorNonce] = useState(0)
  const [forgotOpen, setForgotOpen] = useState(false)
  const { lockedFor, locked, startLockout } = useLockoutCountdown()

  const code = access?.code ?? ''

  const submit = useCallback(
    async (pin: string) => {
      setBusy(true)
      setError(null)
      try {
        const res = await kitLogin(code, pin)
        // El backend setea la cookie de sesión en el mismo request; refresh()
        // hidrata /auth/me antes de que el cuestionario lo necesite.
        const me = await refresh()
        if (!me) throw new Error('No pudimos verificar tu sesión. Revisá la conexión y volvé a ingresar tu código.')
        onLoggedIn({
          displayName: res.display_name || res.username,
          gates: res.gates,
          profilePending: me.pending_profile_fields,
          checkInDone: false,
          afterQuestions: false,
        })
      } catch (e) {
        setDigits('')
        setErrorNonce((n) => n + 1)
        const retry = kitRetryAfterSeconds(e)
        if (retry !== null || (e instanceof ApiError && e.status === 429)) {
          startLockout(retry ?? FALLBACK_LOCKOUT_S)
          setError(null)
        } else if (e instanceof ApiError && e.status === 401) {
          // Mensaje genérico a propósito: no distingue "ese PIN no existe" de
          // "no es el tuyo".
          setError(e.message || 'PIN incorrecto.')
        } else if (e instanceof ApiError && e.status === 404) {
          setError('Este código de club ya no está activo. Avisale al staff.')
        } else {
          setError(e instanceof Error ? e.message : 'Error de red.')
        }
      } finally {
        setBusy(false)
      }
    },
    [code, onLoggedIn, refresh],
  )

  // El submit se dispara desde el efecto y no desde el handler de la tecla
  // para que el sexto dígito llegue a pintarse antes de la llamada.
  const submitted = useRef('')
  useEffect(() => {
    if (digits.length !== PIN_LENGTH) {
      submitted.current = ''
      return
    }
    if (busy || locked) return
    if (submitted.current === digits) return
    submitted.current = digits
    void submit(digits)
  }, [digits, busy, locked, submit])

  const press = (key: number | 'delete') => {
    if (busy || locked) return
    setError(null)
    if (key === 'delete') {
      setDigits((d) => d.slice(0, -1))
      return
    }
    setDigits((d) => (d.length >= PIN_LENGTH ? d : d + String(key)))
  }

  if (forgotOpen) {
    return <ForgotPin code={code} onBack={() => setForgotOpen(false)} />
  }

  return (
    <div className="panel max-w-xl p-2xl">
      {/* De quién es esta tablet y a qué kit apunta: sin esto, quien la
          agarra (o el dev que abre /tablet en su compu) no tiene forma de saber
          qué está mirando. */}
      <p className="m-0 text-xs uppercase tracking-wide text-text-muted">
        {DEVICE_BINDING_COPY.tablet.chrome}
        {access?.club?.name ? ` · ${access.club.name}` : ''}
        {code ? ` · código ${code}` : ''}
      </p>
      <h1 className="mt-xs text-2xl">Entrá con tu PIN</h1>
      <p className="mt-sm text-base text-text-secondary">
        Tu PIN de 6 dígitos, el que configuraste desde el mail. Entrás, resolvés
        lo que tengas pendiente y la tablet vuelve sola acá para el próximo
        jugador.
      </p>

      <PinDots filled={digits.length} shakeKey={errorNonce} className="mt-lg" />

      {/* Slot de alto fijo: los tres estados (lockout, verificando, error)
          se turnan acá adentro y el teclado nunca se mueve. */}
      <PinStatus tone={locked || error ? 'error' : 'muted'} className="mt-md">
        {locked
          ? `Demasiados intentos. Probá de nuevo en ${lockedFor} s.`
          : busy
            ? 'Verificando…'
            : error}
      </PinStatus>

      <PinKeypad onPress={press} disabled={busy || locked} className="mt-md" />

      <div className="mt-lg flex items-center justify-between gap-md text-sm">
        <button type="button" className="btn-ghost px-md py-sm" onClick={() => setForgotOpen(true)}>
          Olvidé mi PIN
        </button>
        {/* Desligar el dispositivo (staff), no logout: es la ÚNICA acción que
            borra el kitAccess. El logout entre jugadores nunca lo toca. Misma
            copia y mismo efecto que el botón del visor. */}
        <button
          type="button"
          className="btn-ghost px-md py-sm text-xs text-text-muted"
          onClick={unbindDevice}
        >
          {DEVICE_BINDING_COPY.tablet.unbind}
        </button>
      </div>
      {isLocalDev() && (
        <DevTestAccessHint
          actionLabel={`Usar PIN de prueba (${DEV_TEST_ACCESS.playerPin})`}
          onAction={() => setDigits(DEV_TEST_ACCESS.playerPin)}
          disabled={busy || locked}
        />
      )}
    </div>
  )
}

function ForgotPin({ code, onBack }: { code: string; onBack: () => void }) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email.includes('@') || busy) return
    setBusy(true)
    setError(null)
    try {
      await requestKitPinResetByEmail(code, email.trim().toLowerCase())
      setSent(true)
    } catch {
      setError('No pudimos enviar la solicitud. Revisá la conexión y probá de nuevo, o pedile ayuda al staff.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="panel max-w-xl p-2xl">
      <h1 className="m-0 text-2xl">Olvidé mi PIN</h1>
      {sent ? (
        <>
          <div className="banner-ok mt-lg">
            Si el mail corresponde a un jugador del club, te llega un link para
            resetearlo.
          </div>
          <button type="button" className="btn-primary mt-lg w-full py-lg text-lg" onClick={onBack}>
            Volver
          </button>
        </>
      ) : (
        <>
          <p className="mt-sm text-base text-text-secondary">
            Escribí tu mail y te mandamos un link para configurar uno nuevo.
          </p>
          {error && <p role="alert" className="banner-err">{error}</p>}
          <form onSubmit={onSubmit} className="mt-lg flex flex-col gap-md">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@dominio.com"
              autoComplete="email"
              autoFocus
              disabled={busy}
              className="input py-md text-lg"
            />
            <button
              type="submit"
              disabled={!email.includes('@') || busy}
              className="btn-primary py-lg text-lg disabled:opacity-50"
            >
              {busy ? 'Enviando…' : 'Mandarme el link'}
            </button>
            <button type="button" className="btn-ghost py-md" onClick={onBack}>
              Volver
            </button>
          </form>
        </>
      )}
    </div>
  )
}
